import React, { useContext } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import AkButton from '@atlaskit/button/standard-button';
import ChevronLeftLargeIcon from '@atlaskit/icon/utility/chevron-left';
import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';
import {
	ADMIN_COMPANY_HUB_PERMISSIONS,
	COMPANY_HUB,
	COMPANY_HUB_EDIT,
	SPACE_SETTINGS_LANDING_PAGE,
} from '@confluence/named-routes';
import { useIsNav4Enabled } from '@confluence/nav4-enabled';
import { useHistory, useRouteDataRef } from '@confluence/route-manager';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils';
import { SPAViewContext } from '@confluence/spa-view-context';

import { getCompanyHubSideNav } from './company-hub/getCompanyHubSideNav';

const i18n = defineMessages({
	spaceSettingsLink: {
		id: 'breadcrumbs.navigation.space-settings-breadcrumb-link.landing-page',
		defaultMessage: 'Space settings',
		description: 'Text for a breadcrumb link back to Space Settings overview page',
	},
	hubLink: {
		id: 'breadcrumbs.navigation.space-settings-breadcrumb-link.hub-name',
		defaultMessage: '{hubName, select, other {{hubName}} undefined {Company Hub}}',
		description: 'Text for a breadcrumb link back to Company Hub Landing Page editor',
	},
	hubSettingsLink: {
		id: 'breadcrumbs.navigation.space-settings-breadcrumb-link.hub-settings',
		defaultMessage: 'Company Hub settings',
		description: 'Text for a breadcrumb link back to the "Company Hub settings" screen',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BreadcrumbLinkStyledText = styled.span({
	fontWeight: token('font.weight.regular'),
	color: token('color.text.subtle'),
	paddingTop: token('space.025'),
	display: 'inline-block',
	textDecoration: 'none',
	'&:hover': {
		textDecoration: 'none',
	},
});

type SpaceSettingsBreadcrumbsProps = {
	spaceKey: string;
	spaceAlias?: string | null;
} & Pick<LinkProps, 'currentSection' | 'source'>;

export function SpaceSettingsBreadcrumbs({
	spaceKey,
	spaceAlias,
	...props
}: SpaceSettingsBreadcrumbsProps) {
	const isNav4Enabled = useIsNav4Enabled();
	const { formatMessage } = useIntl();

	if (isCompanyHubSpaceKey(spaceKey)) {
		return <HubLink {...props} isNav4Enabled={isNav4Enabled} />;
	}

	if (isNav4Enabled) {
		return null;
	}

	// Regular space, not Company Hub
	const href = SPACE_SETTINGS_LANDING_PAGE.toUrl({ spaceKey: spaceAlias || spaceKey });
	const text = formatMessage(i18n.spaceSettingsLink);

	return <Link {...props} href={href} text={text} />;
}

type LinkProps = {
	currentSection: string | null;
	href: string;
	source: string;
	text: string;
};

function Link({ currentSection, href, source, text }: LinkProps) {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onClick = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'spaceSettingsBreadcrumbLink',
				source,
				attributes: {
					currentSection,
				},
			},
		}).fire();
	};

	return (
		<nav>
			<AkButton
				href={href}
				onClick={onClick}
				iconBefore={<ChevronLeftLargeIcon label="" color={token('color.icon')} />}
				appearance="subtle-link"
				spacing="none"
			>
				<BreadcrumbLinkStyledText>{text}</BreadcrumbLinkStyledText>
			</AkButton>
		</nav>
	);
}

type HubLinkProps = {
	isNav4Enabled: boolean;
} & Omit<LinkProps, 'href' | 'text'>;

function HubLink({ isNav4Enabled, ...props }: HubLinkProps) {
	const { current } = useRouteDataRef();
	const routeMatch = { name: current?.routeName, params: current?.routeParams };

	const history = useHistory();
	const { formatMessage } = useIntl();
	const { companyHubName } = useContext(SPAViewContext);

	let href: LinkProps['href'] | undefined;
	let text: LinkProps['text'] | undefined;

	// The breadcrumb in the hub is only necessary when there's no side nav.
	if (isNav4Enabled || getCompanyHubSideNav(routeMatch)) {
		if (fg('company-hub-pseudo-app')) {
			// The Company Hub app-like experience at Team '25 US doesn't have side nav implemented yet so it needs a breadcrumb/link for now.
			if (routeMatch?.name === ADMIN_COMPANY_HUB_PERMISSIONS.name && routeMatch?.params?.tab) {
				href = ADMIN_COMPANY_HUB_PERMISSIONS.toUrl();
				text = formatMessage(i18n.hubSettingsLink);
			}
		} else {
			return null;
		}
	}

	href ??=
		history?.location?.state?.['from'] === COMPANY_HUB_EDIT.name
			? COMPANY_HUB_EDIT.toUrl()
			: COMPANY_HUB.toUrl();
	text ??= formatMessage(i18n.hubLink, { hubName: companyHubName || undefined });

	return <Link {...props} href={href} text={text} />;
}
